import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProjectService } from './project.service';
import { ProjectInfo } from '../models';
import { DraftData, DraftPanelConfig } from '../models/product.model';

/**
 * Clones the editor projects referenced by a draft so a duplicated draft is
 * independent from its source. Shared by the drafts-list duplicate
 * (OrdersComponent.duplicateDraft) and the order-details "duplicate with files"
 * flow (OrderDetailsDialogComponent.executeDuplicate).
 *
 * File references (finishFiles / uploadedPhotos) are NOT touched here — those
 * point at already-uploaded storage files by serverName/md5 and are safe to
 * reuse without copying. Only the .dbproject project references are cloned.
 */
@Injectable({ providedIn: 'root' })
export class DraftDuplicationService {
  private projectService = inject(ProjectService);

  /**
   * Clone a single project reference using ProjectService.duplicateProject().
   * Returns the cloned project info, or null if the reference is empty/invalid.
   *
   * On a suspect clone (backend returns the same id/serverName) it returns null
   * and the caller keeps the original refs — the draft row is still duplicated
   * but the project is shared with the source. This is the deliberate,
   * pre-existing fallback behavior (see callers).
   */
  async cloneProjectRef(
    projectId: number | undefined,
    serverName: string | undefined,
    serverId: number | undefined,
    pathStructureId: number | undefined
  ): Promise<ProjectInfo | null> {
    if (!projectId || !serverName) return null;
    const projectInfo: ProjectInfo = {
      id: projectId,
      serverName,
      serverId: serverId || 0,
      pathStructureId: pathStructureId || 0,
      name: ''
    };
    try {
      const cloned = await firstValueFrom(this.projectService.duplicateProject(projectInfo));
      if (!cloned) return null;
      // Sanity check: a successful clone must produce a NEW projectId. If the
      // backend returns the same id (or missing id), the source's reference
      // would get re-used in the duplicate draft — manifesting as the "clone
      // contaminated with a different project" bug users hit. Refuse to
      // propagate suspect clones; the caller keeps the original refs, which
      // still produces an independent-from-the-source duplicate via the
      // downstream saveDraft call (draft row duplicated, project shared).
      const newId = Number((cloned as any).id);
      if (!newId || newId === projectId) {
        console.warn('[DraftDuplication] Clone endpoint returned suspect id:',
          { sourceId: projectId, returnedId: newId, serverName: (cloned as any).serverName });
        return null;
      }
      if ((cloned as any).serverName === serverName) {
        console.warn('[DraftDuplication] Clone returned same serverName — likely backend issue:',
          { projectId, serverName });
        return null;
      }
      return cloned || null;
    } catch {
      console.warn('[DraftDuplication] Failed to clone project:', projectId, serverName);
      return null;
    }
  }

  /**
   * Clone all project references inside a panel config array.
   */
  async clonePanelProjects(panels: DraftPanelConfig[] | undefined): Promise<void> {
    if (!panels?.length) return;
    for (const panel of panels) {
      if (panel.projectId && panel.projectServerName) {
        const cloned = await this.cloneProjectRef(
          panel.projectId, panel.projectServerName, panel.projectServerId, panel.projectPathStructureId
        );
        if (cloned) {
          panel.projectId = cloned.id;
          panel.projectServerName = cloned.serverName;
          panel.projectServerId = cloned.serverId;
          panel.projectPathStructureId = cloned.pathStructureId;
        }
      }
    }
  }

  /**
   * Clone all project references in draft data (album, cover, panels, standalone
   * product). Mutates the draftData in-place with new project IDs.
   */
  async cloneDraftProjects(draftData: DraftData): Promise<void> {
    if (draftData.isAlbum && draftData.albumConfig) {
      const ac = draftData.albumConfig;

      // Clone album pages project
      const albumClone = await this.cloneProjectRef(
        ac.albumProjectId, ac.albumProjectServerName, ac.albumProjectServerId, ac.albumProjectPathStructureId
      );
      if (albumClone) {
        ac.albumProjectId = albumClone.id;
        ac.albumProjectServerName = albumClone.serverName;
        ac.albumProjectServerId = albumClone.serverId;
        ac.albumProjectPathStructureId = albumClone.pathStructureId;
      }

      // Clone cover project
      const coverClone = await this.cloneProjectRef(
        ac.coverProjectId, ac.coverProjectServerName, ac.coverProjectServerId, ac.coverProjectPathStructureId
      );
      if (coverClone) {
        ac.coverProjectId = coverClone.id;
        ac.coverProjectServerName = coverClone.serverName;
        ac.coverProjectServerId = coverClone.serverId;
        ac.coverProjectPathStructureId = coverClone.pathStructureId;
      }

      // Clone album panel projects
      await this.clonePanelProjects(ac.panels);

      // Clone cover panel projects
      await this.clonePanelProjects(ac.cover?.panels);

    } else if (draftData.productConfig) {
      const pc = draftData.productConfig;

      // Clone main product project
      const originalMainProjectId = pc.projectId;
      const mainClone = await this.cloneProjectRef(
        pc.projectId, pc.projectServerName, pc.projectServerId, pc.projectPathStructureId
      );
      if (mainClone) {
        pc.projectId = mainClone.id;
        pc.projectServerName = mainClone.serverName;
        pc.projectServerId = mainClone.serverId;
        pc.projectPathStructureId = mainClone.pathStructureId;
      }

      // Clone every design of a multi-project standalone draft. The first
      // ref mirrors the singular fields above — reuse that clone instead of
      // cloning the same source twice; the extra designs get their own.
      for (const ref of pc.projects ?? []) {
        if (!ref.projectId || !ref.serverName) continue;
        const cloned = (mainClone && ref.projectId === originalMainProjectId)
          ? mainClone
          : await this.cloneProjectRef(ref.projectId, ref.serverName, ref.serverId, ref.pathStructureId);
        if (cloned) {
          ref.projectId = cloned.id;
          ref.serverName = cloned.serverName;
          ref.serverId = cloned.serverId;
          ref.pathStructureId = cloned.pathStructureId;
        }
      }

      // Clone product panel projects
      await this.clonePanelProjects(pc.panels);
    }
  }
}
